import { Box, BoxProps, Theme, Typography, useTheme } from "@mui/material";
import { ComponentProps, useMemo, useRef } from "react";
import { Chart } from "react-chartjs-2";
import { formatPercentFactor } from "@app/utils";
import {
  ChartTooltip,
  RenderTooltip,
  useChartTooltip,
} from "@app/components/ChartTooltip";
import { ChartJSOrUndefined } from "react-chartjs-2/dist/types";
import { ChartDataset, ChartTypeRegistry } from "chart.js";
import { ChartPlugins, ChartUtils } from "@app/utils/chartUtils";
import { CarChartLegend } from "@app/components/ChartLegend";
import { YearlyForwardReturnsData } from "@app/api/carApi.generated";

type Options = ComponentProps<typeof Chart>["options"];

type DataPoint = number[];

interface BoundariesBoxAndWhiskerData {
  xLabel: string;
  yLabel: string;
  labels: string[];
  datasets: ChartDataset<"boxplot", DataPoint[]>[];
}

const getBoundariesBoxAndWhiskerData = ({
  theme,
  items,
  forPdf,
}: {
  theme: Theme;
  items: YearlyForwardReturnsData[];
  forPdf?: boolean;
}): BoundariesBoxAndWhiskerData => {
  const result: BoundariesBoxAndWhiskerData = {
    labels: items.map((i) => `Year ${i.year}`),
    datasets: [
      {
        type: "boxplot",
        id: "data",
        data: items.map((i) => i.data_points.map((dp) => dp.value)),
        medianColor: "#00000000",
        backgroundColor: theme.palette.chartsColor.blue,
        outlierRadius: 0,
        itemRadius: 0,
        meanRadius: 0,
        borderColor: theme.palette.softBlack,
        borderWidth: 1,
        categoryPercentage: 1,
        barPercentage: 0.3,
      },
    ],
    xLabel: "Forward Returns Over Time",
    yLabel: "Real Forward Return",
  };

  return result;
};

interface SimulationModelsBoundariesBoxAndWhiskerChartProps {
  sx?: BoxProps["sx"];
  items: YearlyForwardReturnsData[];
  isWhiteContext: boolean;
}

export const SimulationModelsBoundariesBoxAndWhiskerChart = ({
  sx,
  items,
  isWhiteContext,
}: SimulationModelsBoundariesBoxAndWhiskerChartProps) => {
  const theme = useTheme();

  const { labels, datasets, yLabel, xLabel } = useMemo(
    () =>
      getBoundariesBoxAndWhiskerData({
        theme,
        items,
      }),
    [theme, items],
  );

  const { tooltipPlugin, tooltipData } = useChartTooltip({
    chartName: "SimulationModelsBoundariesBoxAndWhiskerChart",
    // activeElement,
  });

  const chartRef = useRef<ChartJSOrUndefined<
    keyof ChartTypeRegistry,
    DataPoint[],
    unknown
  > | null>();

  const renderTooltip: RenderTooltip = (datasetIndex, dataIndex) => {
    const dataPoint = datasets.at(datasetIndex)?.data?.at(dataIndex) ?? [];
    console.log({ datasetIndex, dataIndex, dataPoint });
    const minValue = Math.min(...dataPoint);
    const maxValue = Math.max(...dataPoint);

    return (
      <>
        <Box
          sx={{
            fontSize: 15,
            fontWeight: 600,
            color: "black",
            maxWidth: 300,
            gap: 0.5,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Typography variant="par01SemiBold" sx={{ fontSize: 15 }}>
            {`Observations = ${dataPoint?.length ?? "-"}`}
          </Typography>
          <Typography variant="par01Regular" sx={{ fontSize: 13 }}>
            {`Min. 10 Year Forward Return: ${formatPercentFactor(minValue)}`}
          </Typography>
          <Typography variant="par01Regular" sx={{ fontSize: 13 }}>
            {`Max. 10 Year Forward Return: ${formatPercentFactor(maxValue)}`}
          </Typography>
        </Box>
      </>
    );
  };

  const options = useMemo(() => {
    const result: Options = {
      maintainAspectRatio: false,
      animation: {
        duration: 500,
      },
      layout: {
        padding: {
          right: 1, // for some reason right border is cropped, so add right padding to fit it in image
        },
      },
      scales: {
        x: {
          offset: true,

          ticks: {
            padding: 10,
            font: {
              family: theme.typography.fontFamily,
              size: 13,
              weight: 600,
            },
            color: theme.palette.softBlack,
          },
          grid: {
            display: false,
          },
          type: "category",
        },
        y: {
          offset: false,
          type: "linear",
          title: {
            text: yLabel,
            display: true,
            font: {
              family: theme.typography.fontFamily,
              size: 16,
              weight: 600,
            },
          },

          ticks: {
            font: {
              family: theme.typography.fontFamily,
              size: 13,
              weight: 400,
            },
            color: theme.palette.softBlack,
            callback: (value) =>
              typeof value === "number" ? formatPercentFactor(value, 1) : value,
          },
          grid: ChartUtils.getGridOptions({ theme, highlightZero: true }),
        },
      },
      interaction: {
        mode: "point",
        intersect: false,
      },
      plugins: {
        roundedBackground: ChartUtils.getRoundedBackgroundOptions({
          theme,
          isWhiteContext,
        }),
        tooltip: tooltipPlugin,
      },
    };
    return result;
  }, [theme, tooltipPlugin, yLabel, isWhiteContext]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        ...sx,
      }}
    >
      <Box sx={{ display: "flex", zIndex: 1, alignItems: "flex-end" }}>
        <Typography
          variant="par03SemiBold"
          sx={{ fontSize: "19px", mb: 0.5, ml: 10 }}
        >
          {xLabel}
        </Typography>
      </Box>
      <ChartTooltip
        tooltipData={tooltipData}
        renderTooltip={renderTooltip}
        shiftY={0}
        sx={{
          /* Some bug: can't specify height on chart because it starts to grow, so put chart in the box */
          height: 516,
          position: "relative",
        }}
      >
        <Chart
          ref={chartRef}
          type="boxplot"
          datasetIdKey="id"
          data={{ labels, datasets }}
          options={options}
          plugins={[
            ChartPlugins.roundedBackground,
            ChartPlugins.rangeHighlight,
          ]}
        />
      </ChartTooltip>

      <CarChartLegend
        sx={{
          mt: 2.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
          mb: -1,
        }}
        labelVariant="caption"
        items={[
          {
            label: "Forward Returns",
            color: theme.palette.chartsColor.blue,
          },
        ]}
      />
    </Box>
  );
};
